<script setup lang="ts">
import ButtonHighlight from '@/components/buttons/ButtonHighlight.vue';
import ButtonSegmented from '@/components/buttons/ButtonSegmented.vue';
import FieldSurgeon from '@/components/fields/FieldSurgeon.vue';
import FieldPatient from "@/components/fields/FieldPatient.vue";
import FieldYear from '@/components/fields/FieldYear.vue';
import FormFormulaParameters from "@/components/forms/FormFormulaParameters.vue";
import { useRouter, useRoute } from 'vue-router';
import { useFormulaFormStore } from "@/stores/formulaFormStore"
import { useFormulaResultStore } from "@/stores/formulaResultStore"

import NavbarFormula from "@/components/navbar/NavbarFormula.vue";
import IconUpload from "@/components/icons/IconUpload.vue";
import IconWarning from "@/components/icons/IconWarning.vue";
import {getResultFromHttpCalculator} from "@/http/calculatorHttp";
import {type FormulaParameters} from "@/models/FormulaParameters";
import {pdfFirstPageToText} from "@/helpers/pdfHelper";
import {getResultFromHttpAutomation} from "@/http/automationHttp";
import {ref, computed} from "vue";
import {useI18n} from "vue-i18n";
import { computeIcbInfo, getActiveAlerts } from '@/helpers/icbHelper';
const router = useRouter()
const route = useRoute()
const formulaResultStore = useFormulaResultStore()
const formulaFormStore = useFormulaFormStore()

// Modo tórica: recurso em teste — só aparece pra quem entrar com
// ?beta=torica na URL (sem link nenhum na navegação normal). Tirar essa
// trava quando o recurso for liberado pra todo mundo.
const toricBetaEnabled = computed(() => route.query.beta === 'torica')

// Modo tórica: só 1 lente por olho, e o dropdown mostra só lentes tóricas.
// Fica só aqui na página (não precisa ir pro store) — é só um controle de
// exibição do formulário.
const toricMode = ref(false)

function toggleToricMode(newValue: boolean): void
{
    toricMode.value = newValue

    // Ao ligar, limpa os slots 2-4 (senão uma lente escolhida antes de ligar
    // o modo tórica continuaria escondida, mas ainda seria enviada no cálculo).
    if (newValue) {
        for (const eye of [formulaFormStore.formulaForm.od, formulaFormStore.formulaForm.os]) {
            if (!eye) continue
            for (let i = 1; i < eye.aConstantList.length; i++) {
                eye.aConstantList[i] = 0
                eye.idLensList[i] = 0
            }
        }
    }
}


type AutomationResult = {
  "paciente"?: string,
  "cirurgiao"?: string,
  "idade_paciente"?: number,
  "OD"?: {
    "AL"?: number,
    "ACD"?: number,
    "K1"?: number,
    "K2"?: number,
    "LT"?: number,
    "WTW"?: number,
  },
  "OS"?: {
    "AL"?: number,
    "ACD"?: number,
    "K1"?: number,
    "K2"?: number,
    "LT"?: number,
    "WTW"?: number,
  }
}

const loadingFile = ref<boolean>(false);
const showErrorMessage = ref<boolean>(false);
const selectedFileName = ref<string | undefined>(undefined);

function changeOdCheck(): void
{
  if(formulaFormStore.formulaForm.od == undefined) {
    formulaFormStore.formulaForm.od = {
      aConstantList: [],
      idLensList: [], eyeParameters: {
        acd: 0,
        al: 0,
        k: {
          k1: {
            k: 0,
            axis: 0
          },
          k2: {
            k: 0,
            axis: 0
          },
          incision: 0,
          sia: 0
        },
        lt: 0,
        wtw: 0
      }} as FormulaParameters;
  }

  if (formulaFormStore.formulaForm.os?.aConstantList && "aConstantList" in formulaFormStore.formulaForm.od) {
    formulaFormStore.formulaForm.od.idLensList = [...formulaFormStore.formulaForm.os.idLensList]
    formulaFormStore.formulaForm.od.aConstantList = [...formulaFormStore.formulaForm.os.aConstantList]
  }
}

function changeOsCheck(): void
{
  if(formulaFormStore.formulaForm.os == undefined) {
    formulaFormStore.formulaForm.os = {aConstantList: [],
      idLensList: [], eyeParameters: {
        acd: 0,
        al: 0,
        k: {
          k1: {
            k: 0,
            axis: 0
          },
          k2: {
            k: 0,
            axis: 0
          },
          incision: 0,
          sia: 0
        },
        lt: 0,
        wtw: 0
      }} as FormulaParameters;
  }

  if ("aConstantList" in formulaFormStore.formulaForm.os && formulaFormStore.formulaForm.od?.aConstantList) {
    formulaFormStore.formulaForm.os.idLensList = [...formulaFormStore.formulaForm.od.idLensList]
    formulaFormStore.formulaForm.os.aConstantList = [...formulaFormStore.formulaForm.od.aConstantList]
  }
}

async function eventSubmit()
{
  const formulaForm = {...formulaFormStore.formulaForm};

  // A Afacia não pede ACD (olho sem cristalino natural, não dá pra medir
  // com confiança) — só exige AL, K1 e K2. As outras fórmulas continuam
  // exigindo ACD também.
  const acdRequired = formulaFormStore.formulaForm.type !== 'Afacia'

  let newFormulaParameters = formulaForm.od;
  if (
      !(newFormulaParameters?.aConstantList[0]
      && newFormulaParameters?.aConstantList[0] > 0
      && newFormulaParameters?.eyeParameters
      && (!acdRequired || newFormulaParameters?.eyeParameters.acd > 0)
      && newFormulaParameters?.eyeParameters.al > 0
      && newFormulaParameters?.eyeParameters.k.k1.k > 0
      && newFormulaParameters?.eyeParameters.k.k2.k > 0)
  ) {
    formulaForm.od = undefined
  }

  newFormulaParameters = formulaForm.os;
  if (
      !(newFormulaParameters?.aConstantList[0]
          && newFormulaParameters?.aConstantList[0] > 0
          && newFormulaParameters?.eyeParameters
          && (!acdRequired || newFormulaParameters?.eyeParameters.acd > 0)
          && newFormulaParameters?.eyeParameters.al > 0
          && newFormulaParameters?.eyeParameters.k.k1.k > 0
          && newFormulaParameters?.eyeParameters.k.k2.k > 0)
  ) {
    formulaForm.os = undefined
  }

  if((formulaForm.od || formulaForm.os) && !formulaFormStore.formulaForm.loading) {
    if (formulaForm.od)
      formulaForm.od.aConstantList = formulaForm.od?.aConstantList.filter(c => c)
    if (formulaForm.os)
      formulaForm.os.aConstantList = formulaForm.os?.aConstantList.filter(c => c)


    formulaFormStore.formulaForm.loading = true;
    try {
      const result = await getResultFromHttpCalculator(formulaForm);
      formulaFormStore.formulaForm.loading = false;
      if (result) {
        formulaResultStore.save(result)
        // Repassa a query string (?beta=torica) — sem isso a trava do
        // resultado nunca ligaria, mesmo vindo de um /formula liberado.
        router.push({'name': 'result', query: route.query})
      }
    } catch (e) {
      formulaFormStore.formulaForm.loading = false;
    }

  }
}

if (formulaFormStore.data.type === undefined) {
  router.replace({ path: '/', query: route.query });
}

const convertFileToBytes = (file: File) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const arrayBuffer = e.target?.result;
    const bytes = new Uint8Array(arrayBuffer as any);
    pdfFirstPageToText(bytes).then(({textContent, pdfImageUrl}) => {
      if (textContent === "" && pdfImageUrl === "") {
        loadingFile.value = false
        showErrorMessage.value = true
        return
      }
      // Sempre usar a imagem do PDF para obter máxima precisão espacial/visual (evita colunas embaralhadas no texto)
      if (pdfImageUrl !== "") {
        getResultFromImageUrl(pdfImageUrl as string);
        return;
      }
      getResultFromHttpAutomation('text', textContent).then((result: AutomationResult) => {
        formulaFormStore.formulaForm.patient = {name: result.paciente, year: result.idade_paciente}
        formulaFormStore.formulaForm.surgeon = result.cirurgiao

        formulaFormStore.formulaForm.od = {
          aConstantList: formulaFormStore.formulaForm.od ? formulaFormStore.formulaForm.od.aConstantList : [],
          idLensList: formulaFormStore.formulaForm.od ? formulaFormStore.formulaForm.od.idLensList : [],
          eyeParameters: {
              acd: result.OD?.ACD,
              al: result.OD?.AL,
              k: {
                k1: {
                  k: result.OD?.K1,
                  axis: 0
                },
                k2: {
                  k: result.OD?.K2,
                  axis: 0
                },
                incision: 0,
                sia: 0
              },
              lt: result.OD?.LT,
              wtw: result.OD?.WTW
            }} as FormulaParameters;

        formulaFormStore.formulaForm.os = {
          aConstantList: formulaFormStore.formulaForm.os ? formulaFormStore.formulaForm.os.aConstantList : [],
          idLensList: formulaFormStore.formulaForm.os ? formulaFormStore.formulaForm.os.idLensList : [],
          eyeParameters: {
              acd: result.OS?.ACD,
              al: result.OS?.AL,
              k: {
                k1: {
                  k: result.OS?.K1,
                  axis: 0
                },
                k2: {
                  k: result.OS?.K2,
                  axis: 0
                },
                incision: 0,
                sia: 0
              },
              lt: result.OS?.LT,
              wtw: result.OS?.WTW
            }} as FormulaParameters;

        loadingFile.value = false;
      }).catch(() => {
        loadingFile.value = false
        showErrorMessage.value = true
      })
    }).catch(() => {
      loadingFile.value = false
      showErrorMessage.value = true
    })
  };
  reader.readAsArrayBuffer(file);
}

const getResultFromImageUrl = (imageUrl: string) => {
  getResultFromHttpAutomation('image', imageUrl as string).then((result: AutomationResult) => {
    formulaFormStore.formulaForm.patient = {name: result.paciente, year: result.idade_paciente}
    formulaFormStore.formulaForm.surgeon = result.cirurgiao

    formulaFormStore.formulaForm.od = {
      aConstantList: formulaFormStore.formulaForm.od ? formulaFormStore.formulaForm.od.aConstantList : [],
      idLensList: formulaFormStore.formulaForm.od ? formulaFormStore.formulaForm.od.idLensList : [],
      eyeParameters: {
        acd: result.OD?.ACD,
        al: result.OD?.AL,
        k: {
          k1: {
            k: result.OD?.K1,
            axis: 0
          },
          k2: {
            k: result.OD?.K2,
            axis: 0
          },
          incision: 0,
          sia: 0
        },
        lt: result.OD?.LT,
        wtw: result.OD?.WTW
      }} as FormulaParameters;

    formulaFormStore.formulaForm.os = {
      aConstantList: formulaFormStore.formulaForm.os ? formulaFormStore.formulaForm.os.aConstantList : [],
      idLensList: formulaFormStore.formulaForm.os ? formulaFormStore.formulaForm.os.idLensList : [],
      eyeParameters: {
        acd: result.OS?.ACD,
        al: result.OS?.AL,
        k: {
          k1: {
            k: result.OS?.K1,
            axis: 0
          },
          k2: {
            k: result.OS?.K2,
            axis: 0
          },
          incision: 0,
          sia: 0
        },
        lt: result.OS?.LT,
        wtw: result.OS?.WTW
      }} as FormulaParameters;

    loadingFile.value = false;
  }).catch(() => {
    loadingFile.value = false
    showErrorMessage.value = true
  })
}

const processImage = (file: File) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    const base64String = reader.result;
    getResultFromImageUrl(base64String as string);
  };
  reader.readAsDataURL(file);
};

const handleFileChange = (event: any) => {
  const file = event.target.files[0];
  loadingFile.value = true
  showErrorMessage.value = false
  selectedFileName.value = file?.name
  if (file) {
    if (file.type === 'application/pdf') {
      convertFileToBytes(file);
    } else if (file.type === 'image/jpeg' || file.type === 'image/png') {
      processImage(file);
    }
  } else {
    loadingFile.value = false
  }
}

const { t } = useI18n()

const formatNum = (value: number) => {
  return value.toFixed(2).replace('.', ',')
}

const icbInfo = computed(() => {
  return computeIcbInfo(
    formulaFormStore.formulaForm.od?.eyeParameters,
    formulaFormStore.formulaForm.os?.eyeParameters,
    t
  )
})

const activeAlerts = computed(() => {
  return getActiveAlerts(icbInfo.value)
})

</script>

<template>
  <div class="page--with-limit">
    <NavbarFormula></NavbarFormula>
    <h1 class="formula-title"><b>{{formulaFormStore?.formulaForm?.type ? $t("formula.title." + formulaFormStore?.formulaForm?.type) : ''}}</b></h1>
    <hr />
    <section class="upload-exam">
      <label for="arquivo" class="upload-exam__dropzone" :class="{ 'upload-exam__dropzone--loading': loadingFile }">
        <template v-if="loadingFile">
          <span class="upload-exam__spinner"></span>
          <span class="upload-exam__text">
            <span class="upload-exam__label">{{$t("formula.loading")}}</span>
            <span class="upload-exam__hint" v-if="selectedFileName">{{ selectedFileName }}</span>
          </span>
        </template>
        <template v-else>
          <IconUpload class="upload-exam__icon" />
          <span class="upload-exam__text">
            <span class="upload-exam__label">{{$t("formula.loadFile")}}</span>
            <span class="upload-exam__hint">{{ selectedFileName || $t("formula.loadFileHint") }}</span>
          </span>
        </template>
      </label>
      <input id="arquivo" class="upload-exam__input" :disabled="loadingFile" type="file" accept="application/pdf,image/jpeg,image/png" @change="handleFileChange" />

      <p class="upload-exam__status" v-if="showErrorMessage">
        <IconWarning class="upload-exam__status-icon" />
        {{$t("formula.errorMessage")}}
      </p>

      <p class="upload-exam__disclaimer">
        <IconWarning class="upload-exam__disclaimer-icon" />
        {{$t("formula.autoFillDisclaimer")}}
      </p>
    </section>
    <form class="form" @submit.prevent="eventSubmit">
      <section class="form-data-basic">
        <div class="form-data-basic__content">
          <div class="form-data-basic__field-surge">
            <FieldSurgeon v-model="formulaFormStore.formulaForm.surgeon"/>
          </div>
          <div class="form-data-basic__field-patient">
            <FieldPatient v-model="formulaFormStore.formulaForm.patient.name"/>
          </div>
          <div class="form-data-basic__field-year">
            <FieldYear :sufix="$t('formula.form.years')" v-model="formulaFormStore.formulaForm.patient.year"/>
          </div>
        </div>
      </section>

      <div class="toric-mode-toggle" v-if="toricBetaEnabled">
        <ButtonSegmented :model-value="toricMode" @update:model-value="toggleToricMode" :off-label="$t('formula.form.toricModeOff')" :on-label="$t('formula.form.toricModeOn')" />
      </div>

      <div class="form-data-parameters">
        <section class="form-data-parameters__od">
          <FormFormulaParameters :title="$t('formula.form.od')" :text="$t('formula.form.od_right')"
                                 :formulaParameters="formulaFormStore.formulaForm.od"
                                 :alerts="icbInfo?.alerts"
                                 :toric-only="toricMode"
                                 :show-sia="toricBetaEnabled"
                                 @update:formula-parameters="formulaParameters => formulaFormStore.formulaForm.od = formulaParameters"
                                 :copyMessage="$t('formula.form.od_copy')" :copyValues="changeOdCheck"
          />
        </section>

        <section class="form-data-parameters__os">
          <FormFormulaParameters :title="$t('formula.form.os')" :text="$t('formula.form.os_left')"
                                 :formulaParameters="formulaFormStore.formulaForm.os"
                                 :alerts="icbInfo?.alerts"
                                 :toric-only="toricMode"
                                 :show-sia="toricBetaEnabled"
                                 @update:formula-parameters="formulaParameters => formulaFormStore.formulaForm.os = formulaParameters"
                                 :copyMessage="$t('formula.form.os_copy')" :copyValues="changeOsCheck"
          />
        </section>
      </div>

      <!-- ICB global reliability indicator and summary alerts -->
      <section class="icb-container" v-if="icbInfo && activeAlerts.length > 0">
        <!-- Hidden for now as requested: only the ICB card, but alerts remain -->
        <!--
        <div class="icb-card" :class="'icb-card--' + icbInfo.classification">
          <div class="icb-card__header">
            <span class="icb-card__status-dot" :class="'icb-card__status-dot--' + icbInfo.classification"></span>
            <h2>{{ $t('icb.title') }}: <span class="icb-card__score">{{ icbInfo.score }} / 100</span></h2>
          </div>
          <p class="icb-card__text">
            <strong>{{ $t('icb.' + icbInfo.classification) }}</strong>
          </p>
        </div>
        -->

        <div class="alerts-summary">
          <h3>{{ $t('icb.summary_title') }}</h3>
          <ul class="alerts-summary__list">
            <li v-for="alert in activeAlerts" :key="alert.key" class="alerts-summary__item" :class="'alerts-summary__item--' + alert.status">
              <div class="alerts-summary__content">
                <div class="alerts-summary__title-line">
                  <span class="icb-dot" :class="'icb-dot--' + alert.status"></span>
                  <strong>{{ alert.label }}</strong>
                </div>
                <div class="alerts-summary__medidas-line">
                  Medidas: OD={{ formatNum(alert.od) }} {{ alert.unit }} / OE= {{ formatNum(alert.os) }} {{ alert.unit }} (dif: {{ formatNum(alert.diff) }} {{ alert.unit }})
                </div>
                <div class="alerts-summary__msg-line">
                  {{ alert.message }}
                </div>
                <div class="alerts-summary__limits-line">
                  Limites: 
                  <span class="icb-dot icb-dot--green"></span> &lt; {{ formatNum(alert.limits.green) }} {{ alert.unit }} 
                  <span class="icb-dot icb-dot--yellow"></span> &gt;= {{ formatNum(alert.limits.yellowMin) }} {{ alert.unit }} e &lt;= {{ formatNum(alert.limits.yellowMax) }} {{ alert.unit }} 
                  <span class="icb-dot icb-dot--red"></span> &gt; {{ formatNum(alert.limits.red) }} {{ alert.unit }}
                </div>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <div class="formula-form__footer">
        <ButtonHighlight v-if="!formulaFormStore.formulaForm.loading">{{$t("formula.calculate")}}</ButtonHighlight>
        <ButtonHighlight v-if="formulaFormStore.formulaForm.loading">
          {{$t("formula.loading")}}
        </ButtonHighlight>
      </div>
    </form>
  </div>
</template>

<style scoped lang="sass">
@use "../assets/colors/colors"

$break-layout: 1130px

.page--with-limit
  position: relative

.form-data-basic
  &__title
    display: none

  &__content
    display: flex
    flex-direction: column
    gap: 0.6em

  @media (min-width: 700px)
    &__content
      flex-direction: row
      gap: 3.70%

    &__field-surge
      width: 20%

    &__field-patient
      width: 59.6%

    &__field-year
      width: 13%

.toric-mode-toggle
  display: flex
  justify-content: center
  padding-top: 2.4rem
  padding-left: 0.8em
  padding-right: 0.8em

  @media (min-width: $break-layout)
    padding-left: 2em
    padding-right: 2em

.form-data-parameters
  padding-top: 3.2rem
  display: flex
  flex-direction: row
  gap: 1.5em

  @media (min-width: $break-layout)
    gap: 3.2em

  &__od
    color: colors.$tertiary
    flex: 1 1 50%
    min-width: 0

  &__os
    color: colors.$quaternary
    flex: 1 1 50%
    min-width: 0

.formula-form
  &__footer
    padding-top: 2rem
    text-align: center
    margin-bottom: 100px

.formula-title
  font-size: 2em

.upload-exam
  padding: 2.4rem 0 1rem
  display: flex
  flex-direction: column
  gap: 1rem
  width: 100%

  &__input
    // Visualmente escondido, mas continua acessível/focável via teclado —
    // o clique é disparado pelo <label for="arquivo"> estilizado ao lado.
    position: absolute
    width: 1px
    height: 1px
    padding: 0
    margin: -1px
    overflow: hidden
    clip: rect(0, 0, 0, 0)
    white-space: nowrap
    border: 0

  &__dropzone
    display: flex
    align-items: center
    gap: 1.2rem
    width: 100%
    box-sizing: border-box
    padding: 1.6rem 2rem
    border: 2px dashed colors.$border
    border-radius: 8px
    background-color: colors.$secondary-contrast
    cursor: pointer
    transition: border-color 0.15s, background-color 0.15s

    &:hover
      border-color: colors.$primary
      background-color: colors.$secondary-contrast-strong

    &:has(+ .upload-exam__input:focus-visible)
      border-color: colors.$primary
      outline: 2px solid colors.$primary
      outline-offset: 2px

    &--loading
      cursor: progress
      opacity: 0.7

  &__icon
    flex-shrink: 0
    width: 2.6rem
    height: 2.6rem
    fill: colors.$primary

  &__text
    display: flex
    flex-direction: column
    gap: 0.2rem
    // Permite que __hint trunque com reticências em vez de empurrar a
    // largura do dropzone quando o nome do arquivo é muito longo.
    min-width: 0

  &__label
    font-size: 1.6rem
    font-weight: 500
    color: colors.$secondary

  &__hint
    display: block
    font-size: 1.2rem
    color: colors.$secondary
    opacity: 0.7
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  &__status
    display: flex
    align-items: center
    gap: 0.6rem
    font-size: 1.3rem
    margin: 0
    color: colors.$error

  &__status-icon
    width: 1.6rem
    height: 1.6rem
    fill: colors.$error

  &__spinner
    width: 1.4rem
    height: 1.4rem
    border-radius: 50%
    border: 2px solid colors.$primary-contrast
    border-top-color: colors.$primary
    animation: upload-exam-spin 0.7s linear infinite

  &__disclaimer
    display: flex
    align-items: flex-start
    gap: 0.8rem
    margin: 0.4rem 0 0
    padding: 1rem 1.2rem
    font-size: 1.3rem
    line-height: 1.4
    color: #7d6000
    background-color: #fffef0
    border-left: 4px solid #d4a017
    border-radius: 4px

  &__disclaimer-icon
    flex-shrink: 0
    width: 1.8rem
    height: 1.8rem
    margin-top: 0.1rem
    fill: #d4a017

@keyframes upload-exam-spin
  to
    transform: rotate(360deg)

.icb-container
  margin-top: 2rem
  margin-bottom: 0.5rem
  display: flex
  flex-direction: column
  gap: 2rem

.icb-card
  padding: 2rem
  border-radius: 4px
  border: 1px solid colors.$border
  background-color: colors.$secondary-contrast-strong
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.25)
  display: flex
  flex-direction: column
  gap: 0.8rem

  &--excellent
    border-left: 6px solid #00890C
  &--review
    border-left: 6px solid #d4a017
  &--moderate
    border-left: 6px solid #e4b863
  &--high
    border-left: 6px solid #831717

  &__header
    display: flex
    align-items: center
    gap: 1rem
    
    h2
      font-size: 1.8rem
      font-weight: 600
      color: colors.$secondary
      
  &__status-dot
    width: 12px
    height: 12px
    border-radius: 50%
    
    &--excellent
      background-color: #00890C
    &--review
      background-color: #d4a017
    &--moderate
      background-color: #e4b863
    &--high
      background-color: #831717

  &__score
    font-weight: 700

  &__text
    font-size: 1.4rem
    color: colors.$secondary

.alerts-summary
  display: flex
  flex-direction: column
  gap: 1.2rem
  
  h3
    font-size: 1.6rem
    font-weight: 600
    color: colors.$secondary
    border-bottom: 1px solid colors.$primary-and-secundary-contrast
    padding-bottom: 0.5rem

  &__list
    list-style: none
    padding: 0
    margin: 0
    display: flex
    flex-direction: column
    gap: 1rem

  &__item
    display: flex
    align-items: flex-start
    gap: 1rem
    padding: 1.2rem
    border-radius: 6px
    background-color: colors.$secondary-contrast
    border: 1px solid colors.$border
    
    &--green
      border-left: 4px solid #22c55e
    &--yellow
      border-left: 4px solid #eab308
    &--red
      border-left: 4px solid #ef4444

  &__content
    display: flex
    flex-direction: column
    gap: 0.4rem
    font-size: 1.3rem
    color: colors.$secondary
    
    strong
      font-size: 1.4rem
      color: colors.$secondary

  &__title-line
    display: flex
    align-items: center
    gap: 0.5rem

.icb-dot
  width: 12px
  height: 12px
  border-radius: 50%
  display: inline-block
  margin: 0 4px
  vertical-align: middle
  flex-shrink: 0
  -webkit-print-color-adjust: exact
  print-color-adjust: exact

  &--green
    background-color: #22c55e
  &--yellow
    background-color: #eab308
  &--red
    background-color: #ef4444
</style>
